package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.i18n.t
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * A keyboard shortcut, drawn as the keys it is actually made of.
 *
 * [Space.XXS] between them rather than [Space.XS] — they are one gesture, and a gap wide enough to
 * read as a list of separate keys is wrong for something pressed in one motion.
 */
val LChordStyle = CssStyle.base {
    Modifier
        .styleModifier { property("display", "inline-flex") }
        .alignItems(AlignItems.Center)
        .gap(Space.XXS)
}

/**
 * One [LKbd] per key, with the word a listener needs between them.
 *
 * **The gap is a space to the eye and nothing at all to a screen reader.** `⌘` and `K` in adjacent
 * `<kbd>` elements are announced as "command K" — two nouns with no relationship, which is also how
 * "⌘ then K" and "⌘ or K" would sound. The visually-hidden connective is the only thing that says
 * which of those a reader is being told, and it costs nothing on screen because the gap is already
 * doing that job there.
 *
 * Renders nothing for an empty list rather than an empty box: a control with no shortcut passes
 * `null` through [chordKeys] and gets here with nothing to draw, and a 4px sliver of padding beside
 * a menu label is worse than the absence it is standing in for.
 *
 * Take the keys from [chordKeys] or [commandChordKeys] rather than splitting a string at the call
 * site — the difference between `"⌘K"` and `"Ctrl K"` is this design system's to know, not a
 * caller's.
 */
@Composable
fun LChord(
    keys: List<String>,
    modifier: Modifier = Modifier,
) {
    if (keys.isEmpty()) return
    Span(LChordStyle.toModifier().then(modifier).toAttrs()) {
        keys.forEachIndexed { index, key ->
            if (index > 0) {
                Span(LVisuallyHiddenStyle.toModifier().toAttrs()) {
                    Text(t("ds.ds.lchord.then", " then "))
                }
            }
            LKbd(key)
        }
    }
}
